import axios from 'axios'
import { defineStore } from 'pinia'
import type {
  IAdverseEventSettings,
  IAdverseEventTypeCreatePayload,
  IAdverseEventTypeTreeNode,
  IAdverseEventTypeUpdatePayload,
  IAdverseProbabilityThreshold,
  IAdverseProbabilityThresholdCreatePayload,
  IAdverseProbabilityThresholdUpdatePayload,
  IProbabilityConsequenceResponse,
  IRiskSelectOption,
} from '../types/IAdverseSettings'
import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'

/**
 * Стор настроек модуля «Нежелательные события» (Adverse Events Settings).
 *
 * Управляет:
 * - Деревом категорий и видов нежелательных событий (AdverseEventType);
 * - Настройками видимости видов (все / свои / глобальные);
 * - CRUD-операциями для видов событий (создание, редактирование, перемещение в дереве);
 * - CRUD-операциями для порогов вероятности событий (AdverseEventProbabilityThreshold);
 * - Справочником опций вероятностей и последствий (/api/adverse/probability-consequence/);
 * - Состоянием загрузки и индикацией сохранения.
 */
export const useAdverseSettingsStore = defineStore('adverseSettingsStore', {
  state: () => ({
    /** Дерево видов нежелательных событий */
    treeData: [] as IAdverseEventTypeTreeNode[],
    /** Объект настроек нежелательных событий */
    settings: {} as IAdverseEventSettings,
    /** Активный выбранный режим отображения видов ('all' | 'custom' | 'expert') */
    activeSettingValue: null as any,
    /** Строка поискового запроса по видам НС */
    searchQuery: '',

    // ==========================================
    // Пороги вероятности и Опции рисков
    // ==========================================

    /** Список всех настроенных порогов вероятности */
    probabilityThresholds: [] as IAdverseProbabilityThreshold[],
    /** Доступные опции уровней вероятности (из /api/adverse/probability-consequence/) */
    probabilityOptions: [] as IRiskSelectOption[],
    /** Доступные опции уровней последствий (из /api/adverse/probability-consequence/) */
    consequenceOptions: [] as IRiskSelectOption[],
  }),

  actions: {
    /**
     * 1. Загрузка дерева видов нежелательных событий
     *
     * @param params Дополнительные GET-параметры (например: { is_daomed_main: true/false, search: string })
     * @returns Массив узлов дерева видов НС
     */
    async fetchAdverseEventTypeTree(params?: Record<string, any>): Promise<IAdverseEventTypeTreeNode[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<{ results: IAdverseEventTypeTreeNode[] }>(
          onboardingApiUrl('adverse_event', '/adverse/adverse-event-type/tree-nodes/'),
          { params },
        )
        const tree = response.data?.results || response.data || []
        this.treeData = Array.isArray(tree) ? tree : []
        return this.treeData
      } catch (error) {
        console.error('❌ Ошибка загрузки дерева видов НС:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить дерево видов нежелательных событий'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 2. Загрузка настроек отображения видов НС
     *
     * @param settingsUrl URL ручки настроек (по умолчанию '/api/adverse/settings/')
     * @returns Объект конфигурации настроек
     */
    async fetchAdverseSettings(settingsUrl: string = '/api/adverse/settings/'): Promise<IAdverseEventSettings> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const url = settingsUrl.startsWith('http') ? settingsUrl : `${BASE_URL}${settingsUrl}`
        const response = await axios.get<{ result: IAdverseEventSettings }>(url)
        const data = response.data.result || response.data
        this.settings = data
        return data
      } catch (error) {
        console.error('❌ Ошибка загрузки настроек видов НС:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить настройки нежелательных событий'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 2.1 Обновление конфигурации настроек нежелательных событий
     *
     * @param payload Данные настроек (например: { event_types: 'all' | 'custom' | 'expert' })
     * @returns Обновленный объект настроек
     */
    async updateAdverseSettings(payload: Partial<IAdverseEventSettings>): Promise<IAdverseEventSettings> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<{ result: IAdverseEventSettings }>(
          `${BASE_URL}/api/adverse/settings/`,
          payload,
        )
        const data = response.data.result || response.data
        this.settings = { ...this.settings, ...data }
        feedbackStore.showToast({
          type: 'success',
          message: 'Настройки модуля успешно сохранены',
          time: 4000,
        })
        return this.settings
      } catch (error) {
        console.error('❌ Ошибка сохранения настроек видов НС:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить настройки нежелательных событий'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 3. Создание нового вида нежелательного события
     *
     * @param payload Данные для создания нового вида НС
     * @returns Созданный объект вида НС
     */
    async createAdverseEventType(payload: IAdverseEventTypeCreatePayload): Promise<any> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/adverse/adverse-event-type/`, payload)
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания вида НС:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать вид нежелательного события'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 4. Обновление существующего вида нежелательного события
     *
     * @param id Идентификатор обновляемого вида НС
     * @param payload Данные для обновления
     * @returns Обновленный объект вида НС
     */
    async updateAdverseEventType(id: string, payload: IAdverseEventTypeUpdatePayload): Promise<any> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(`${BASE_URL}/api/adverse/adverse-event-type/${id}/`, payload)
        return response.data
      } catch (error) {
        console.error('❌ Ошибка обновления вида НС:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения вида нежелательного события'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Переключает активность вида НС (PATCH is_active) без глобального лоадера.
     * Оптимистично обновляет узел в treeData.
     */
    async toggleAdverseEventTypeActive(id: string, isActive: boolean): Promise<boolean> {
      const findAndSet = (
        nodes: IAdverseEventTypeTreeNode[],
        value: boolean,
      ): IAdverseEventTypeTreeNode | null => {
        for (const node of nodes) {
          if (String(node.id) === String(id)) {
            node.is_active = value
            return node
          }
          if (node.children?.length) {
            const found = findAndSet(node.children, value)
            if (found) return found
          }
        }
        return null
      }

      const previous = (() => {
        const walk = (nodes: IAdverseEventTypeTreeNode[]): boolean | undefined => {
          for (const node of nodes) {
            if (String(node.id) === String(id)) return node.is_active
            if (node.children?.length) {
              const nested = walk(node.children)
              if (nested !== undefined) return nested
            }
          }
          return undefined
        }
        return walk(this.treeData)
      })()

      findAndSet(this.treeData, isActive)

      try {
        await axios.patch(`${BASE_URL}/api/adverse/adverse-event-type/${id}/`, { is_active: isActive })
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'success',
          message: isActive ? 'Вид активирован' : 'Вид деактивирован',
          time: 3000,
        })
        return true
      } catch (error) {
        if (previous !== undefined) findAndSet(this.treeData, previous)
        console.error('❌ Ошибка переключения активности вида НС:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить активность вида'),
          time: 3000,
        })
        return false
      }
    },

    /**
     * 5. Перемещение вида НС в дереве (изменение parent_id)
     *
     * @param draggedId ID перемещаемого узла
     * @param targetId ID целевого родительского узла (или null / 'root-virtual' для переноса в корень)
     * @returns Результат обновления узла
     */
    async moveAdverseEventType(draggedId: string, targetId: string | null): Promise<any> {
      const parentId = targetId === 'root-virtual' || targetId === null ? null : targetId
      return this.updateAdverseEventType(draggedId, { parent_id: parentId })
    },

    // ==========================================
    // Экшены порогов вероятности и рисков
    // ==========================================

    /**
     * 6. Загрузка списка порогов вероятности
     *
     * @returns Список порогов вероятности
     */
    async fetchProbabilityThresholds(): Promise<IAdverseProbabilityThreshold[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<IAdverseProbabilityThreshold[]>(
          onboardingApiUrl('adverse_event', '/adverse/probability-threshold/'),
        )
        const list = Array.isArray(response.data) ? response.data : []
        // Сортируем по возрастанию вероятности (0..4)
        list.sort((a, b) => a.probability - b.probability)
        this.probabilityThresholds = list
        return list
      } catch (error) {
        console.error('❌ Ошибка загрузки порогов вероятности:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить пороги вероятности'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 7. Создание нового порога вероятности
     *
     * @param payload Данные для создания порога
     * @returns Созданный объект порога
     */
    async createProbabilityThreshold(
      payload: IAdverseProbabilityThresholdCreatePayload,
    ): Promise<IAdverseProbabilityThreshold> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IAdverseProbabilityThreshold>(
          `${BASE_URL}/api/adverse/probability-threshold/`,
          payload,
        )
        feedbackStore.showToast({
          type: 'success',
          message: 'Порог вероятности успешно создан',
          time: 4000,
        })
        await this.fetchProbabilityThresholds()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания порога вероятности:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать порог вероятности'),
          time: 6000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 8. Обновление существующего порога вероятности
     *
     * @param id Идентификатор порога
     * @param payload Данные для обновления
     * @returns Обновленный объект порога
     */
    async updateProbabilityThreshold(
      id: number,
      payload: IAdverseProbabilityThresholdUpdatePayload,
    ): Promise<IAdverseProbabilityThreshold> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<IAdverseProbabilityThreshold>(
          `${BASE_URL}/api/adverse/probability-threshold/${id}/`,
          payload,
        )
        feedbackStore.showToast({
          type: 'success',
          message: 'Порог вероятности успешно обновлен',
          time: 4000,
        })
        await this.fetchProbabilityThresholds()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка обновления порога вероятности:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить порог вероятности'),
          time: 6000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 9. Удаление порога вероятности
     *
     * @param id Идентификатор удаляемого порога
     */
    async deleteProbabilityThreshold(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/adverse/probability-threshold/${id}/`)
        feedbackStore.showToast({
          type: 'success',
          message: 'Порог вероятности успешно удален',
          time: 4000,
        })
        await this.fetchProbabilityThresholds()
      } catch (error) {
        console.error('❌ Ошибка удаления порога вероятности:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить порог вероятности'),
          time: 6000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * 10. Загрузка опций справочника вероятностей и последствий
     */
    async fetchProbabilityAndConsequence(): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<IProbabilityConsequenceResponse>(
          `${BASE_URL}/api/adverse/probability-consequence/`,
        )

        const probabilityOptions: IRiskSelectOption[] = (response.data.probability || []).map(([value, label]) => ({
          value,
          label,
        }))

        const consequenceOptions: IRiskSelectOption[] = (response.data.consequence || []).map(([value, label]) => ({
          value,
          label,
        }))

        this.probabilityOptions = probabilityOptions
        this.consequenceOptions = consequenceOptions
      } catch (error) {
        console.error('❌ Ошибка загрузки опций вероятности и последствий:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить справочники рисков'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})


